/**
 * Shared rendering helpers for the tactics board drawing layers.
 *
 * Moved out of `tactics2d/DrawingLayer2D.tsx` so the new 3D-native
 * `DrawingLayer3D` can reuse them without depending on the 2D module.
 * Both layers stay consistent on colour treatment (zone fill derived
 * from stroke) and measure-label formatting (region-aware m vs yd).
 */

/**
 * Convert a CSS hex colour ('#86efac' or '#abc') to an `rgba()` string
 * at the requested alpha. The zone fill picks up the coach's stroke
 * colour at a fixed transparency without depending on `fill-opacity`
 * (which would also fade the stroke).
 *
 * Falls back to the input verbatim on unparseable input so an explicit
 * `rgba(...)` / named colour still renders.
 */
export function hexWithAlpha(hex: string, alpha: number): string {
  if (typeof hex !== 'string' || hex[0] !== '#') return hex;
  const body = hex.slice(1);
  const expand = body.length === 3
    ? body.split('').map((c) => c + c).join('')
    : body;
  if (expand.length !== 6) return hex;
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Region-aware distance label for the §7 measure tool. UK pack shows
 * metres to one decimal place ("8.5 m"); US pack shows yards rounded
 * to the nearest whole unit ("9 yd"). Storage is always metres — the
 * formatter is purely a display concern.
 */
export function formatMeasureLabel(
  distanceMetres: number,
  unit: 'metres' | 'yards',
): string {
  if (!Number.isFinite(distanceMetres) || distanceMetres < 0) {
    return unit === 'metres' ? '0.0 m' : '0 yd';
  }
  if (unit === 'yards') {
    return `${Math.round(distanceMetres * 1.09361)} yd`;
  }
  return `${distanceMetres.toFixed(1)} m`;
}
